/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IActionContext } from '@msdyn365-commerce/core';
import { Cart, CartsDataActions } from '@msdyn365-commerce/retail-proxy';

import { ICartActionResultWithCart } from './cart-action-result';

/**
 * Sets price for the given cart line with an invoice. Used to complete partial invoice payments.
 * @param cart - Cart where the invoice is located.
 * @param cartLineId - The cart line with the invoice for which to update the price.
 * @param price - The new value of price of the invoice.
 * @param actionContext - The action context from SDK.
 * @returns Result of the operation with the details.
 */
export async function setInvoiceLinePriceInternal(
    cart: Readonly<Cart>, cartLineId: string | null, price: number, actionContext: IActionContext): Promise<ICartActionResultWithCart> {

    if (!cart.Version) {
        actionContext.telemetry.warning('Unable to set invoice line price, Cart Version could not be found');
        return { cart: undefined, status: 'FAILED', substatus: 'NOCARTVERSION' };
    }

    try {
        const newCart = await CartsDataActions.setInvoiceLinePriceAsync({ callerContext: actionContext }, cart.Id, cartLineId, price, cart.Version);
        return { cart: newCart, status: 'SUCCESS' };
    } catch (error) {
        const errorInstance = error as Error;
        actionContext.telemetry.trace(error as string);
        actionContext.telemetry.trace('Unable to set invoice line price');

        return {
            cart: undefined,
            status: 'FAILED',
            errorDetails: { LocalizedMessage: errorInstance.message }
        };
    }
}

export default setInvoiceLinePriceInternal;
